// ============================================================
//  src/emails.js — Envío de correos via EmailJS
//
//  Usa 2 templates:
//    template_o874ll7 → admin (nueva solicitud)
//    template_yckw9lf → usuario (recibida + resolución)
//
//  EmailJS se carga desde CDN en index.html.
//  La Public Key se inicializa una sola vez en inicializarEmailJS().
// ============================================================

import { getRuntimeFlags } from './runtime-flags.js';

// ── Configuración ─────────────────────────────────────────────

const EMAILJS_CONFIG = {
    publicKey:       'Tbge15YYEq7UFcCU7',
    serviceId:       'service_q309p0l',
    templateAdmin:   'template_o874ll7',
    templateUsuario: 'template_yckw9lf',
    emailAdmin:      'cx4dpg@gmail.com',
    linkAdmin:       `${window.location.origin}/?admin=1`,
};

// ── Inicialización ────────────────────────────────────────────

/**
 * Inicializa EmailJS con la Public Key.
 * Llamar una sola vez al arrancar la app.
 */
export function inicializarEmailJS() {
    if (typeof emailjs === 'undefined') {
        console.warn('[Email] EmailJS no está cargado.');
        return;
    }
    emailjs.init({ publicKey: EMAILJS_CONFIG.publicKey });
    console.log('[Email] EmailJS inicializado.');
}

// ── Envíos ────────────────────────────────────────────────────

/**
 * Notifica al admin que llegó una nueva solicitud.
 * @param {Object} solicitud - Datos de la solicitud
 * @param {Object} usuario   - { displayName, email }
 */
export async function notificarAdminNuevaSolicitud(solicitud, usuario) {
    await _enviar(EMAILJS_CONFIG.templateAdmin, {
        email_to:        EMAILJS_CONFIG.emailAdmin,
        nombre_concurso: solicitud.nombre,
        web_url:         solicitud.webUrl          || '—',
        modos:           (solicitud.modos  || []).join(', ') || '—',
        bandas:          (solicitud.bandas || []).join(', ') || '—',
        descripcion:     solicitud.descripcion     || '—',
        nombre_usuario:  usuario.displayName       || 'Sin nombre',
        email_usuario:   usuario.email             || '—',
        link_admin:      EMAILJS_CONFIG.linkAdmin,
    });
}

/**
 * Confirma al usuario que su solicitud fue recibida.
 * @param {Object} solicitud
 * @param {Object} usuario - { displayName, email }
 */
export async function notificarUsuarioSolicitudRecibida(solicitud, usuario) {
    await _enviar(EMAILJS_CONFIG.templateUsuario, {
        email_to:        usuario.email,
        nombre_usuario:  usuario.displayName || 'Operador',
        nombre_concurso: solicitud.nombre,
        web_url:         solicitud.webUrl    || '—',
        modos:           (solicitud.modos || []).join(', ') || '—',
        icono_estado:    '📡',
        titulo_estado:   'RECIBIDA',
        color_estado:    '#00d4aa',
        bg_estado:       'rgba(0,212,170,0.1)',
        estado_mensaje:  'fue recibida correctamente y está pendiente de revisión',
        label_comentario:'Próximos pasos',
        comentario:      'El administrador revisará tu solicitud a la brevedad. Recibirás otro correo cuando sea procesada.',
    });
}

/**
 * Notifica al usuario la resolución de su solicitud (aprobada o rechazada).
 * @param {Object} solicitud  - Datos de la solicitud
 * @param {Object} usuario    - { displayName, email }
 * @param {'aprobada'|'rechazada'} estado
 * @param {string} comentario - Comentario del admin
 */
export async function notificarUsuarioResolucion(solicitud, usuario, estado, comentario) {
    const esAprobada = estado === 'aprobada';

    await _enviar(EMAILJS_CONFIG.templateUsuario, {
        email_to:        usuario.email,
        nombre_usuario:  usuario.displayName || 'Operador',
        nombre_concurso: solicitud.nombre,
        web_url:         solicitud.webUrl    || '—',
        modos:           (solicitud.modos || []).join(', ') || '—',
        icono_estado:    esAprobada ? '✅' : '❌',
        titulo_estado:   esAprobada ? 'APROBADA' : 'RECHAZADA',
        color_estado:    esAprobada ? '#3fb950' : '#f85149',
        bg_estado:       esAprobada ? 'rgba(63,185,80,0.1)' : 'rgba(248,81,73,0.1)',
        estado_mensaje:  esAprobada
            ? 'fue <strong>aprobada</strong> y ya está disponible en el catálogo'
            : 'fue <strong>rechazada</strong> por el administrador',
        label_comentario: esAprobada ? 'Comentario del administrador' : 'Motivo del rechazo',
        comentario:      comentario || (esAprobada ? 'Solicitud aprobada.' : 'Sin comentarios adicionales.'),
    });
}

/**
 * Notifica a un usuario que hay un resultado o certificado disponible.
 * Reutiliza template_yckw9lf con parámetros adaptados.
 * @param {Object} usuario   - { displayName, email }
 * @param {'resultado_disponible'|'certificado_disponible'} tipo
 * @param {Object} edicion   - { concurso, año, modo, archivoUrl }
 */
export async function notificarArchivoDisponible(usuario, tipo, edicion) {
    if (!usuario.email) return;

    const esResultado = tipo === 'resultado_disponible';
    const etiqueta    = esResultado ? 'RESULTADOS' : 'CERTIFICADOS';
    const icono       = esResultado ? '📊' : '🏅';

    await _enviar(EMAILJS_CONFIG.templateUsuario, {
        email_to:        usuario.email,
        nombre_usuario:  usuario.displayName || 'Operador',
        nombre_concurso: edicion.concurso,
        web_url:         edicion.archivoUrl || '—',
        modos:           edicion.modo || '—',
        icono_estado:    icono,
        titulo_estado:   etiqueta,
        color_estado:    '#00d4aa',
        bg_estado:       'rgba(0,212,170,0.1)',
        estado_mensaje:  `ya están disponibles para <strong>${edicion.concurso} ${edicion.año} — ${edicion.modo}</strong>`,
        label_comentario: 'Dónde verlos',
        comentario:      'Ingresa a Contest Manager y abre la edición correspondiente.',
    });
}

// ── Helper privado ────────────────────────────────────────────

async function _enviar(templateId, parametros) {
    const flags = await getRuntimeFlags();
    if (flags.emergencyMode || !flags.emailsEnabled) {
        console.warn('[Email] Envío bloqueado — emergencyMode o emailsEnabled=false.');
        return;
    }
    if (typeof emailjs === 'undefined') {
        console.warn('[Email] EmailJS no disponible — correo no enviado.');
        return;
    }
    try {
        await emailjs.send(
            EMAILJS_CONFIG.serviceId,
            templateId,
            parametros
        );
        console.log(`[Email] Correo enviado. Template: ${templateId}`);
    } catch (error) {
        // Error silencioso — el correo es una mejora, no crítico
        console.error('[Email] Error al enviar correo:', error);
    }
}
